import { useAuth } from 'react-oidc-context';
import { PermissionDto } from 'src/models/account';
import { useSettings } from './useSettings';
import { Products } from '../utils/getProductLevel';

// consumed once by uiflow list views on mount to reset any active search terms on impersonation start/exit
export const CLEAR_LIST_SEARCH_ON_MOUNT_KEY = 'uiflow-clear-list-search-on-mount';

export interface ImpersonationObject {
  isFusionUser: boolean;
  companyName: string;
  tenantId: string;
  partnerId: string;
  roleId: string;
  productLevel: Products;
  permissions: PermissionDto[];
  subdomain: string;
}

interface UseImpersonateSessionDataReturn {
  isImpersonating: boolean;
  impersonateData: ImpersonationObject;
  permissions: string[];
}

/**
 * Hook to get the impersonate session data, providing access to the session object, isImpersonating, and permissions converted to token style.
 *
 * @returns UseImpersonateSessionData
 */
const useImpersonateSessionData = (): UseImpersonateSessionDataReturn => {
  const auth = useAuth();
  const user = auth.user;
  const loggedInUserId = user?.profile.tenantUserId as string;
  const sessionObject = sessionStorage.getItem(`impersonate-${loggedInUserId}`);
  const { settings: { isImpersonating = false } = {} } = useSettings() ?? {};

  const impersonateData: ImpersonationObject = JSON.parse(sessionObject ?? '{}');
  const permissions = convertImpersonatePermissionsToTokenStyle(impersonateData?.permissions ?? []);

  /**
   * Converts the array of permissions from PermissionDto to token style. i.e '<menuItemComponent.code>.<uiComponent.name>.<permissionType>'
   *
   * @param incomingPermissions
   * @returns string[]
   */
  function convertImpersonatePermissionsToTokenStyle(incomingPermissions: PermissionDto[]): string[] {
    const permissionArray: string[] = [];
    for (const permission of incomingPermissions) {
      const {
        canCreate: CanCreate = false,
        canRead: CanRead = false,
        canUpdate: CanUpdate = false,
        canDelete: CanDelete = false,
        menuItemComponentCode = '',
        uicomponentName = ''
      } = permission;

      Object.entries({ CanCreate, CanRead, CanUpdate, CanDelete })
        .filter(([, value]) => Boolean(value))
        .forEach(([permissionType]) => {
          permissionArray.push(
            [menuItemComponentCode?.toLowerCase(), uicomponentName?.toLowerCase(), permissionType]
              .filter(entry => Boolean(entry))
              .join('.')
          );
        });
    }

    return permissionArray;
  }

  return {
    isImpersonating,
    impersonateData,
    permissions
  };
};

export default useImpersonateSessionData;
